import React, { useState, useRef } from 'react';
import { 
  Button, 
  FormControl, 
  InputBase, 
  MenuItem, 
  Paper, 
  Select, 
  Typography, 
  styled as muiStyled,
  Popover,
  Box,
  Divider,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Modal,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'next-i18next';
import { 
  getLocationByLocationTypeID, 
  getProjectOnlyData, 
  getProvinceWithAmphur, 
  getLocationType, 
  getLocationBranchByLocationID 
} from '../../utils/requests';

// นำเข้าข้อมูล JSON
import tbCategory from '../../../app/components/search-by-options/tb_category.json';
import tbLocationType from '../../../app/components/search-by-options/tb_location_type.json';
import tbActions from '../../../app/components/search-by-options/tb_actions.json';
import tbNonlocationPrice from '../../../app/components/search-by-options/tb_nonlocationPrice.json';

// นำเข้าคอมโพเนนต์ย่อย
import LocationModal from './LocationModal';
import LocationPopover from './LocationPopover';
import FilterPopover from './FilterPopover';

// นำเข้า styles
import {
  SearchContainer,
  StyledSelect,
  SearchInput,
  FilterButton,
  SearchButton,
} from './styles';

// นำเข้า utils และ types
import { LocationItem, FilterAnchorEl, ModalView, SearchData } from './types';
import { 
  formatThaiCurrency, 
  getProvinceData,
  getLocationTypeData,
  loadLocationData,
  loadLocationBranches,
} from './utils';

const SearchBar = () => {
  const { t, i18n } = useTranslation('common');
  const locale = i18n.language || 'th';
  
  // กรองข้อมูล actions จาก tbActions
  const filteredActions = tbActions
    .filter(action => action.id !== 1 && action.id !== 8) // ไม่เอา "รวม" ทั้ง id 1 และ 8
    .sort((a, b) => a.id - b.id);
  
  // สร้าง mapping ระหว่าง action id กับคีย์ใน i18n
  const actionMapping = {
    2: 'project',
    3: 'sell',
    4: 'rent',
    5: 'lease',
    6: 'mortgageOrRedemption', // รวม mortgage และ redemption
    7: 'mortgageOrRedemption'  // รวม mortgage และ redemption
  };
  
  // กรองเฉพาะ category ที่มี category_Status=1 และเรียงตาม category_Sort
  // จำกัดให้แสดงเฉพาะ 12 รายการแรก
  const filteredCategories = tbCategory
    .filter(cat => cat.category_Status === 1)
    .sort((a, b) => a.category_Sort - b.category_Sort)
    .slice(0, 12);
  
  // สร้าง mapping ระหว่าง category_ID กับคีย์ใน i18n
  const categoryMapping = {
    1: 'condo',
    2: 'townhouse',
    3: 'house',
    4: 'homeoffice',
    5: 'land',
    6: 'space',
    7: 'apartment',
    8: 'warehouse',
    9: 'commercial',
    10: 'shop',
    11: 'hotel',
    13: 'apartment', // Service apartment
    15: 'commercial', // Office Building
    16: 'factory',
    17: 'resort',
    34: 'mansion',
    38: 'twinhome'
  };
  
  // State สำหรับการค้นหา
  const [searchText, setSearchText] = useState('');
  const [propertyType, setPropertyType] = useState(t('sell')); // default ให้เป็น "ขาย" (id 3)
  const [actionType, setActionType] = useState('');
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(10000000);
  const [minInputValue, setMinInputValue] = useState('0');
  const [maxInputValue, setMaxInputValue] = useState('10,000,000');
  
  // State สำหรับ filter
  const [filterAnchorEl, setFilterAnchorEl] = useState<FilterAnchorEl>(null);
  const [locationAnchorEl, setLocationAnchorEl] = useState<FilterAnchorEl>(null);
  
  // State สำหรับ modal
  const [openModal, setOpenModal] = useState(false);
  const [locationTypeId, setLocationTypeId] = useState<number>(0);
  const [locationData, setLocationData] = useState<LocationItem[]>([]);
  const [loadingLocationData, setLoadingLocationData] = useState(false);
  const [modalSearchValue, setModalSearchValue] = useState(''); // เพิ่ม state เก็บค่าที่กำลังค้นหาใน modal
  
  // State สำหรับการเลือกสถานที่
  const [selectedLocation, setSelectedLocation] = useState<LocationItem | null>(null);
  const [selectedSubLocation, setSelectedSubLocation] = useState<LocationItem | null>(null);
  const [locationSearchText, setLocationSearchText] = useState('');
  
  // State สำหรับเก็บข้อมูลที่เตรียมไว้
  const [locationTypes, setLocationTypes] = useState<LocationItem[]>([]);
  
  // Ref สำหรับ component
  const searchInputRef = useRef(null);
  const filterButtonRef = useRef(null);
  
  // State สำหรับการจัดการการแสดงผลใน modal
  const [modalView, setModalView] = useState<ModalView>('main');
  const [selectedMainOption, setSelectedMainOption] = useState<LocationItem | null>(null);
  
  // สถานะสำหรับควบคุมการพิมพ์ใน input
  const [isSearchDisabled, setIsSearchDisabled] = useState(false);
  
  // ช่วงราคาสำหรับให้เลือก
  const priceRangesMin = [0, 500000, 1000000, 1500000, 2000000];
  const priceRangesMax = [500000, 1000000, 1500000, 2000000];

  // ฟังก์ชันสำหรับเปลี่ยนแปลงประเภทอสังหา
  const handlePropertyChange = (event) => {
    setPropertyType(event.target.value);
  };

  // ฟังก์ชันสำหรับเปลี่ยนแปลงประเภททรัพย์สิน
  const handleActionChange = (event) => {
    setActionType(event.target.value);
  };

  // ฟังก์ชันจัดการกับการพิมพ์ใน input
  const handleSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isSearchDisabled) return; // ถ้าปิดการพิมพ์ไว้ ให้ไม่ทำอะไร
    
    setSearchText(e.target.value);
    
    // ถ้า dropdown ยังไม่เปิด ให้เปิด
    if (!locationAnchorEl && e.target.value) {
      setLocationAnchorEl(e.currentTarget);
    }
    
    // ถ้าข้อความว่างและมี dropdown เปิดอยู่ ให้ปิด
    if (locationAnchorEl && !e.target.value) {
      setLocationAnchorEl(null);
    }
  };

  // ฟังก์ชันสำหรับการค้นหา
  const handleSearch = () => {
    const searchData: SearchData = {
      propertyType,
      actionType,
      searchText,
      priceMin,
      priceMax,
      location: selectedLocation,
      subLocation: selectedSubLocation
    };
    
    console.log('Searching with:', searchData);
    
    // สร้าง URL สำหรับการค้นหาคล้ายกับ SearchByOptions
    const urlParts: string[] = [];
    const queryParams: string[] = [];
    
    // เพิ่มประเภททรัพย์สิน (ต้องมาก่อนประเภทอสังหา)
    if (propertyType) {
      // หาชื่อภาษาไทยของ action เพื่อใช้ใน URL ต้องการใช้ action_TH
      const actionObj = filteredActions.find(action => {
        const i18nKey = actionMapping[action.id];
        return i18nKey && t(i18nKey) === propertyType;
      });
      
      // ถ้าเจอใช้ค่าภาษาไทยจาก action_TH
      if (actionObj) {
        urlParts.push(actionObj.action_TH);
      } else {
        // ถ้าไม่เจอให้ใช้ค่าจาก propertyType เอง (เพื่อความปลอดภัย)
        urlParts.push(propertyType);
      }
    } else {
      urlParts.push(t('estate'));
    }
    
    // เพิ่มประเภทอสังหา
    if (actionType) {
      // หาชื่อภาษาไทยของ category เพื่อใช้ใน URL
      const categoryObj = filteredCategories.find(category => {
        const i18nKey = categoryMapping[category.category_ID];
        return i18nKey && t(i18nKey) === actionType;
      });
      
      // ถ้าเจอใช้ค่าภาษาไทยจาก category_Name_TH
      if (categoryObj) {
        urlParts.push(categoryObj.category_Name_TH);
      } else if (actionType === t('all')) {
        urlParts.push('ทั้งหมด');
      } else {
        // ถ้าไม่เจอให้ใช้ค่าจาก actionType เอง
        urlParts.push(actionType);
      }
    } else {
      urlParts.push('ทั้งหมด');
    }
    
    // เพิ่มข้อมูลที่ตั้ง
    if (selectedLocation) {
      // ถ้าไม่ใช่จังหวัด ให้เพิ่มชื่อประเภทสถานที่ เช่น "รถไฟฟ้า"
      if (locationSearchText !== t('location_categories.province') && locationSearchText) {
        urlParts.push(locationSearchText);
      }
      
      // เพิ่มชื่อสถานที่หลัก
      if (selectedLocation.label) {
        urlParts.push(selectedLocation.label);
      }
      
      // ถ้ามีสถานที่ย่อย และไม่ใช่ "ทั้งหมด"
      if (selectedSubLocation && 
          !selectedSubLocation.value.toString().startsWith('all_') && 
          selectedSubLocation.label) {
        urlParts.push(selectedSubLocation.label);
      }
    }
    
    // เพิ่ม query parameters สำหรับราคา
    if (priceMin > 0) {
      queryParams.push(`startPrice=${priceMin}`);
    }
    
    if (priceMax < 1000000000) {
      queryParams.push(`endPrice=${priceMax}`);
    }
    
    // สร้าง URL จากส่วนต่างๆ
    let searchURL = '/' + urlParts.filter(part => part && part.trim() !== '').join('/');
    
    // รวม query parameters เข้ากับ URL
    if (queryParams.length > 0) {
      searchURL += `?${queryParams.join('&')}`;
    }
    
    // แสดง URL ในรูปแบบที่สวยงาม
    console.log('-------------------------------------------');
    console.log('ลิงก์ที่จะไปเมื่อกดค้นหา (ยังไม่ redirect):');
    console.log(searchURL);
    console.log('URL ที่แยกส่วน:', urlParts);
    console.log('-------------------------------------------');
    
    // ปิดการนำทางไปยัง URL การค้นหา
    if (typeof window !== 'undefined') {
      window.location.href = searchURL;
    }
  };

  // ฟังก์ชันเปิด/ปิด Filter Popover
  const handleFilterOpen = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // ฟังก์ชันเปิด/ปิด Location Popover
  const handleLocationOpen = (event) => {
    setLocationAnchorEl(event.currentTarget);
  };

  const handleLocationClose = () => {
    setLocationAnchorEl(null);
  };

  // ฟังก์ชันจัดการกับการเลือกราคา
  const handleMinPriceSelect = (price) => {
    setPriceMin(price);
    setMinInputValue(formatThaiCurrency(price));
  };

  const handleMaxPriceSelect = (price) => {
    setPriceMax(price);
    setMaxInputValue(formatThaiCurrency(price));
  };

  const handleMinInputChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setMinInputValue(value);
    setPriceMin(Number(value) || 0);
  };

  const handleMaxInputChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setMaxInputValue(value);
    setPriceMax(Number(value) || 0);
  };

  const handleFilterApply = () => {
    console.log('Applying filters', { priceMin, priceMax });
    handleFilterClose();
  };

  // ฟังก์ชันสำหรับล้างข้อความค้นหา
  const handleClearSearch = () => {
    setModalSearchValue(''); // เคลียร์ค่าที่กำลังพิมพ์ในช่องค้นหา
    
    // โหลดข้อมูลใหม่ทั้งหมด
    const loadFullData = async () => {
      setLoadingLocationData(true);
      
      if (modalView === 'main') {
        // โหลดข้อมูลหลักใหม่
        if (locationSearchText === t('location_categories.province')) {
          const provinceData = await getProvinceData();
          setLocationData(provinceData);
        } else {
          const data = await loadLocationData(locationTypeId || 0, locale);
          setLocationData(data);
        }
      } else if (modalView === 'sub' && selectedMainOption) {
        // กรณีอยู่ในหน้าย่อยและมีตัวเลือกหลักที่เลือกไว้
        if (selectedMainOption.children) {
          // ถ้ามี children อยู่แล้ว ให้คืนค่าเดิม
          const originalMainOption = locationData.find(item => item.value === selectedMainOption.value);
          if (originalMainOption) {
            setSelectedMainOption(originalMainOption);
          } else {
            // ถ้าไม่พบ originalMainOption ให้โหลดข้อมูลใหม่
            await handleSelectLocationWithBranches(selectedMainOption);
          }
        } else {
          // ถ้ายังไม่มี children ให้โหลดข้อมูลสาขา
          await handleSelectLocationWithBranches(selectedMainOption);
        }
      }
      
      setLoadingLocationData(false);
    };
    
    loadFullData();
  };

  // ฟังก์ชันสำหรับบันทึกการตั้งค่า
  const handleSaveSettings = () => {
    if (modalView === 'main' && locationData.length > 0) {
      // ถ้าอยู่ในหน้าหลัก ให้เลือกตัวเลือกแรก
      handleSelectLocation(locationData[0]);
    } else if (modalView === 'sub' && selectedMainOption) {
      // ถ้าอยู่ในหน้าย่อย แต่ยังไม่ได้เลือกตัวเลือกย่อย ให้เลือก "ทั้งหมด"
      const selectAllOption = {
        value: `all_${selectedMainOption.value}`,
        label: locationSearchText === t('location_categories.province') ? t('all_districts') : t('all_in').replace('%s', selectedMainOption.label || ''),
        typeId: selectedMainOption.typeId,
        lat: selectedMainOption.lat,
        lng: selectedMainOption.lng
      };
      handleSelectSubLocation(selectedMainOption, selectAllOption);
    }
    handleModalClose();
  };

  // ฟังก์ชันสำหรับการคลิกที่ input
  const handleSearchInputClick = (e) => {
    if (isSearchDisabled) {
      // ถ้าปิดการพิมพ์ไว้แล้ว การคลิกจะเป็นการเปิด modal แทน
      handleLocationOpen(e);
    } else {
      // ถ้ายังไม่ได้ปิดการพิมพ์ ให้ทำงานตามปกติ
      if (!locationAnchorEl) {
        setLocationAnchorEl(e.currentTarget);
      }
    }
  };

  // ฟังก์ชันสำหรับลบการเลือกและเปิดให้พิมพ์ได้อีกครั้ง
  const handleClearSelection = () => {
    setSelectedLocation(null);
    setSelectedSubLocation(null);
    setSearchText('');
    setIsSearchDisabled(false);
  };

  // ฟังก์ชันสำหรับการคลิกเลือกตัวเลือกประเภทสถานที่
  const handleLocationClick = async (locationType: string) => {
    if (locationAnchorEl) {
      setLocationAnchorEl(null);
    }
    setOpenModal(true);
    setLocationSearchText(locationType); // ยังใช้เป็นชื่อของ modal
    setModalSearchValue(''); // เคลียร์ค่าที่กำลังพิมพ์ในช่องค้นหา
    setModalView('main'); // รีเซ็ตเป็นการแสดงผลหลัก
    setSelectedMainOption(null); // รีเซ็ตตัวเลือกหลัก
    
    setLoadingLocationData(true);
    
    // ดึงข้อมูลตามประเภทสถานที่
    if (locationType === t('location_categories.province')) {
      try {
        const provinceData = await getProvinceData();
        setLocationData(provinceData);
        setLocationTypeId(0);
      } catch (error) {
        console.error('Error loading province data:', error);
      }
    } else {
      // กรณีเป็นประเภทสถานที่อื่นๆ
      const locationTypeObj = tbLocationType.find(
        type => {
          const typeName = locale === 'en' ? type.location_type_name_EN : type.location_type_name_TH;
          return typeName === locationType || t(`location_categories.${type.location_type_name_EN.toLowerCase().replace(/ /g, '_')}`) === locationType;
        }
      );
      
      if (locationTypeObj) {
        const typeId = Number(locationTypeObj.location_type_ID);
        setLocationTypeId(typeId);
        const data = await loadLocationData(typeId, locale);
        setLocationData(data);
      }
    }
    
    setLoadingLocationData(false);
  };

  // ฟังก์ชันสำหรับเช็คว่าสถานที่มี sub options หรือไม่
  const hasSubLocations = async (location: LocationItem): Promise<boolean> => {
    try {
      console.log('ตรวจสอบตัวเลือกรองสำหรับ:', location);

      // กรณีไม่มี location หรือ value ไม่ถูกต้อง
      if (!location || !location.value) {
        console.log('ไม่มีข้อมูล location หรือ value ไม่ถูกต้อง');
        return false;
      }
      
      // กรณีที่ชื่อเป็น "ไม่มีชื่อ" หรือ "ทั้งหมด" จะไม่มีตัวเลือกรอง
      if (location.label === "ไม่มีชื่อ" || location.label === "ทั้งหมด") {
        console.log('เป็นรายการ "ไม่มีชื่อ" หรือ "ทั้งหมด" ไม่มีตัวเลือกรอง');
        return false;
      }
      
      // กรณีที่ children ถูกตั้งค่าเป็น null โดยเฉพาะ (ไม่ต้องตรวจสอบเพิ่มเติม)
      if (location.children === null) {
        console.log('location.children เป็น null โดยตรง ไม่มีตัวเลือกรอง');
        return false;
      }
      
      // กรณีที่เป็นจังหวัด จะมี sub options เสมอ (อำเภอ)
      if (locationSearchText === t('location_categories.province')) {
        console.log('เป็นจังหวัด มีตัวเลือกรองเสมอ');
        return true;
      }

      // กรณีมี children ที่ไม่ใช่ array ว่าง
      if (location.children && location.children.length > 0) {
        console.log('มี children พร้อมอยู่แล้ว:', location.children.length);
        return true;
      }
      
      // ตรวจสอบจาก API โดยตรง
      if (location.value) {
        // ลองโหลดข้อมูลสาขาเพื่อตรวจสอบว่ามีจริงๆ หรือไม่
        console.log('กำลังตรวจสอบข้อมูลสาขาสำหรับ ID:', location.value);
        try {
          const branches = await loadLocationBranches(location.value);
          console.log('ผลการตรวจสอบข้อมูลสาขา:', branches ? branches.length : 0, 'รายการ');
          
          // มีตัวเลือกรองเมื่อมีสาขามากกว่า 0
          return branches && branches.length > 0;
        } catch (error) {
          console.error('เกิดข้อผิดพลาดในการตรวจสอบข้อมูลสาขา:', error);
          return false;
        }
      }
      
      // กรณีอื่นๆ ไม่มี sub options
      console.log('ไม่มีเงื่อนไขใดเข้าข่าย ไม่มีตัวเลือกรอง');
      return false;
    } catch (error) {
      console.error('เกิดข้อผิดพลาดในการตรวจสอบตัวเลือกรอง:', error);
      return false;
    }
  };

  // แก้ไขฟังก์ชัน handleMainOptionClick
  const handleMainOptionClick = async (location: LocationItem) => {
    setLoadingLocationData(true);
    
    // ทำการ log ข้อมูลสถานที่ที่คลิก
    console.log('คลิกที่ตัวเลือกหลัก:', location);
    
    // กรณีพิเศษ: ชื่อเป็น "ไม่มีชื่อ" หรือ "ทั้งหมด" ไม่ต้องไปตรวจสอบ ให้เลือกเลย
    if (location.label === "ไม่มีชื่อ" || location.label === "ทั้งหมด") {
      console.log(`ชื่อเป็น "${location.label}" ไม่มีตัวเลือกรอง เลือกเลย`);
      setSelectedLocation(location);
      setSelectedSubLocation(null);
      setSearchText(location.label);
      setIsSearchDisabled(true);
      setOpenModal(false);
      handleLocationClose();
      setLoadingLocationData(false);
      return;
    }
    
    // ตรวจสอบว่ามี sub options หรือไม่
    const hasSubs = await hasSubLocations(location);
    console.log('ผลการตรวจสอบตัวเลือกรอง:', hasSubs ? 'มี' : 'ไม่มี');
    
    if (hasSubs) {
      // ถ้ามี sub options ให้ไปที่หน้า sub
      setSelectedMainOption(location);
      setModalView('sub');
      
      // ตรวจสอบว่ามี sub options อยู่แล้วหรือไม่ หากไม่มีให้ดึงข้อมูล
      if (!location.children || location.children.length === 0) {
        console.log('ไม่มีข้อมูล children ทำการโหลดข้อมูลสาขา');
        await handleSelectLocationWithBranches(location);
      }
    } else {
      // ถ้าไม่มี sub options ให้เลือก option หลักเลย
      console.log('ไม่มีตัวเลือกรอง เลือกตัวเลือกหลักและปิด modal');
      setSelectedLocation(location);
      setSelectedSubLocation(null);
      setSearchText(location.label);
      setIsSearchDisabled(true); // ไม่ให้พิมพ์ได้อีกหลังเลือก
      setOpenModal(false); // ปิด Modal
      handleLocationClose(); // ปิด popover ด้วย
    }
    
    setLoadingLocationData(false);
  };
  
  // เพิ่มฟังก์ชัน handleBackToMain สำหรับกลับไปหน้าตัวเลือกหลัก
  const handleBackToMain = () => {
    setModalView('main');
    setSelectedMainOption(null);
  };
  
  // แก้ไขฟังก์ชัน handleModalClose เพื่อรีเซ็ตค่าต่างๆ
  const handleModalClose = () => {
    setOpenModal(false);
    setModalView('main');
    setSelectedMainOption(null);
    setLocationSearchText('');
    setModalSearchValue(''); // รีเซ็ตค่าที่กำลังพิมพ์ในช่องค้นหา
  };

  // แก้ไข handleSelectLocationWithBranches
  const handleSelectLocationWithBranches = async (location: LocationItem) => {
    try {
      setLoadingLocationData(true);
      
      if (location.children) {
        // ถ้ามีข้อมูลย่อยอยู่แล้ว ไม่ต้องโหลดใหม่
        return;
      }
      
      // ถ้ายังไม่มีข้อมูลย่อย ดึงข้อมูลสาขาของสถานที่
      const branches = await loadLocationBranches(location.value);
      
      if (branches && branches.length > 0) {
        // อัพเดทข้อมูลสถานที่โดยเพิ่มสาขาเป็น children
        const updatedLocation = {
          ...location,
          children: branches
        };
        
        // อัพเดทรายการสถานที่
        const updatedLocationList = locationData.map(item => 
          item.value === location.value ? updatedLocation : item
        );
        
        setLocationData(updatedLocationList);
        setSelectedMainOption(updatedLocation);
      } else {
        // กรณีไม่มีสาขา ให้เลือกตัวเลือกหลักและปิด modal
        console.log('ไม่พบข้อมูลสาขา ให้เลือกตัวเลือกหลักเลย');
        setSelectedLocation(location);
        setSelectedSubLocation(null);
        setSearchText(location.label);
        setIsSearchDisabled(true);
        setOpenModal(false);
        handleLocationClose();
        setModalView('main');
      }
    } catch (error) {
      console.error('Error loading location branches:', error);
      
      // กรณีเกิดข้อผิดพลาด ให้เลือกตัวเลือกหลักและปิด modal
      setSelectedLocation(location);
      setSelectedSubLocation(null);
      setSearchText(location.label);
      setIsSearchDisabled(true);
      setOpenModal(false);
      handleLocationClose();
      setModalView('main');
    } finally {
      setLoadingLocationData(false);
    }
  };

  // แก้ไข handleLocationSearch
  const handleLocationSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    setModalSearchValue(searchValue); // เก็บค่าที่พิมพ์ในช่องค้นหาของ modal
    
    // ค้นหาใหม่ทุกครั้งที่มีการพิมพ์หรือลบตัวอักษร
    if (modalView === 'main') {
      if (!searchValue.trim()) {
        // ถ้าไม่มีข้อความค้นหา โหลดข้อมูลทั้งหมดใหม่
        const loadData = async () => {
          setLoadingLocationData(true);
          if (locationSearchText === t('location_categories.province')) {
            const provinceData = await getProvinceData();
            setLocationData(provinceData);
          } else {
            const data = await loadLocationData(locationTypeId || 0, locale);
            setLocationData(data);
          }
          setLoadingLocationData(false);
        };
        loadData();
      } else {
        // ค้นหาจากตัวเลือกหลัก
        // โหลดข้อมูลเต็มก่อนแล้วค่อยกรอง (ในกรณีที่ข้อมูลถูกกรองไปแล้วก่อนหน้า ให้โหลดข้อมูลใหม่
        const loadAndFilter = async () => {
          // ถ้ามีข้อมูลน้อย หรือเป็นการกรองข้อมูลจากการค้นหาก่อนหน้า ให้โหลดข้อมูลใหม่
          if (locationData.length < 3) {
            setLoadingLocationData(true);
            let fullData: LocationItem[] = [];
            
            if (locationSearchText === t('location_categories.province')) {
              fullData = await getProvinceData();
            } else {
              fullData = await loadLocationData(locationTypeId || 0, locale);
            }
            
            // กรองข้อมูลตามคำค้นหา
            const filtered = fullData.filter(item => 
              item.label && item.label.toLowerCase().includes(searchValue.toLowerCase())
            );
            
            setLocationData(filtered);
            setLoadingLocationData(false);
          } else {
            // ถ้ามีข้อมูลมากพอ ให้กรองจากข้อมูลที่มีอยู่
            const filtered = locationData.filter(item => 
              item.label && item.label.toLowerCase().includes(searchValue.toLowerCase())
            );
            setLocationData(filtered);
          }
        };
        
        loadAndFilter();
      }
    } else if (modalView === 'sub' && selectedMainOption && selectedMainOption.children) {
      // ค้นหาจากตัวเลือกย่อย
      if (!searchValue.trim()) {
        // ถ้าไม่มีข้อความค้นหา แสดงข้อมูลทั้งหมด
        // ไม่ต้องโหลดใหม่เพราะ children มีอยู่แล้ว
      } else {
        // กรองข้อมูลตามคำค้นหา
        const filteredChildren = selectedMainOption.children.filter(item =>
          item.label && item.label.toLowerCase().includes(searchValue.toLowerCase())
        );
        
        // สร้าง object ใหม่เพื่อไม่ให้มีผลกระทบต่อข้อมูลเดิม
        const updatedMainOption = {
          ...selectedMainOption,
          children: filteredChildren
        };
        
        // อัพเดทข้อมูลในรายการหลัก
        const updatedLocationData = locationData.map(item =>
          item.value === selectedMainOption.value ? updatedMainOption : item
        );
        
        setLocationData(updatedLocationData);
        setSelectedMainOption(updatedMainOption);
      }
    }
  };

  // ฟังก์ชันสำหรับเลือกสถานที่จากรายการ
  const handleSelectLocation = (location: LocationItem) => {
    setSelectedLocation(location);
    if (!location.children) {
      // ถ้าไม่มีสถานที่ย่อย ให้แสดงเฉพาะชื่อสถานที่หลัก
      setSearchText(location.label);
      setIsSearchDisabled(true); // ไม่ให้พิมพ์ได้อีกหลังเลือก
      handleModalClose();
      handleLocationClose();
    }
  };

  // ฟังก์ชันสำหรับเลือกสถานที่ย่อย
  const handleSelectSubLocation = (parentLocation: LocationItem, subLocation: LocationItem) => {
    setSelectedLocation(parentLocation);
    setSelectedSubLocation(subLocation);
    
    // ถ้าเลือก "ทั้งหมด" ให้แสดงเฉพาะชื่อสถานที่หลัก
    if (subLocation.value.toString().startsWith('all_')) {
      setSearchText(parentLocation.label);
    } else {
      // แสดงทั้งชื่อสถานที่หลักและสถานที่ย่อย
      setSearchText(`${parentLocation.label} - ${subLocation.label}`);
    }
    
    setIsSearchDisabled(true); // ไม่ให้พิมพ์ได้อีกหลังเลือก
    handleModalClose();
    handleLocationClose();
  };

  const filterOpen = Boolean(filterAnchorEl);
  const locationOpen = Boolean(locationAnchorEl);

  const menuProps = {
    PaperProps: {
      style: {
        borderRadius: '10px',
        marginTop: '8px',
        boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)',
        padding: '16px',
      },
    },
    MenuListProps: {
      style: {
        padding: 0,
      },
    },
  };

  const menuItemStyle = {
    paddingTop: '8px', 
    paddingBottom: '8px', 
    paddingLeft: '0', 
    paddingRight: '0',
    position: 'relative',
  };

  const menuItemWithDividerStyle = {
    ...menuItemStyle,
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: '0',
      right: '0',
      height: '1px',
      backgroundColor: '#E0E0E0',
    }
  };

  const locationCategories = [
    t('location_categories.province'),
    t('location_categories.mass_transit'),
    t('location_categories.shopping_mall'),
    t('location_categories.university'),
    t('location_categories.road'),
    t('location_categories.hospital'),
    t('location_categories.park'),
    t('location_categories.airport'),
    t('location_categories.popular_location'),
  ];

  return (
    <SearchContainer>
      <StyledSelect
        value={propertyType}
        onChange={handlePropertyChange}
        displayEmpty
        IconComponent={KeyboardArrowDownIcon}
        MenuProps={menuProps}
        renderValue={(selected) => {
          if (!selected) {
            return <Typography variant="body2" color="#919192">{t('sell')}</Typography>;
          }
          return <Typography variant="body2">{String(selected)}</Typography>;
        }}
      >
        {/* แสดงรายการจาก filteredActions */}
        {filteredActions.map((action, index) => {
          const isLast = index === filteredActions.length - 1;
          const itemStyle = isLast ? menuItemStyle : menuItemWithDividerStyle;
          
          // ใช้ mapping เพื่อแปลงเป็นคีย์ที่ถูกต้อง
          const i18nKey = actionMapping[action.id] || '';
          const translatedAction = i18nKey ? t(i18nKey) : (locale === 'en' ? action.action_EN : action.action_TH);
          
          return (
            <MenuItem 
              key={action.id} 
              value={translatedAction} 
              sx={itemStyle}
            >
              {translatedAction}
            </MenuItem>
          );
        })}
      </StyledSelect>

      <StyledSelect
        value={actionType}
        onChange={handleActionChange}
        displayEmpty
        IconComponent={KeyboardArrowDownIcon}
        MenuProps={menuProps}
        renderValue={(selected) => {
          if (!selected) {
            return <Typography variant="body2" color="#919192">{t('estate')}</Typography>;
          }
          return <Typography variant="body2">{String(selected)}</Typography>;
        }}
      >
        {/* ตัวเลือก "ทั้งหมด" */}
        <MenuItem value={t('all')} sx={menuItemWithDividerStyle}>{t('all')}</MenuItem>
        
        {/* แสดงรายการจาก filteredCategories ที่เรียงตาม category_Sort แล้ว */}
        {filteredCategories.map((category, index) => {
          const isLast = index === filteredCategories.length - 1;
          const itemStyle = isLast && filteredCategories.length === index + 1 ? menuItemStyle : menuItemWithDividerStyle;
          
          // ใช้ mapping เพื่อแปลงเป็นคีย์ที่ถูกต้อง
          const i18nKey = categoryMapping[category.category_ID] || category.category_Name_EN.toLowerCase().replace(/ /g, '');
          const translatedCategory = t(i18nKey);
          
          return (
            <MenuItem 
              key={category.category_ID} 
              value={translatedCategory} 
              sx={itemStyle}
            >
              {translatedCategory}
            </MenuItem>
          );
        })}
      </StyledSelect>

      <SearchInput 
        placeholder={t('search_location')} 
        value={searchText}
        onChange={handleSearchTextChange}
        onClick={handleSearchInputClick}
        ref={searchInputRef}
        endAdornment={
          isSearchDisabled && searchText ? (
            <InputAdornment position="end">
              <IconButton 
                onClick={handleClearSelection}
                edge="end"
                sx={{ color: '#919192' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null
        }
      />
      
      {/* Location Popover */}
      <LocationPopover
        open={locationOpen}
        anchorEl={locationAnchorEl}
        onClose={handleLocationClose}
        locationCategories={locationCategories}
        onLocationClick={handleLocationClick}
      />

      {/* Location Modal */}
      <LocationModal
        open={openModal}
        onClose={handleModalClose}
        locationSearchText={locationSearchText}
        onSearchChange={handleLocationSearch}
        loadingLocationData={loadingLocationData}
        locationData={locationData}
        modalView={modalView}
        selectedMainOption={selectedMainOption}
        onMainOptionClick={handleMainOptionClick}
        onBackToMain={handleBackToMain}
        onSelectSubLocation={handleSelectSubLocation}
        onClearSearch={handleClearSearch}
        onSaveSettings={handleSaveSettings}
        isSearchDisabled={isSearchDisabled}
        searchValue={modalSearchValue}
        hasSubLocations={hasSubLocations}
      />

      <FilterButton 
        ref={filterButtonRef}
        variant="contained" 
        sx={{
          backgroundColor: '#ffffff',
          color: '#919192',
          border: '1px solid #D3D3D3',
        }}
        startIcon={<MenuOutlinedIcon />}
        onClick={handleFilterOpen}
      >
        {t('more_filter')}
      </FilterButton>

      {/* Filter Popover */}
      <FilterPopover
        open={filterOpen}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        priceMin={priceMin}
        priceMax={priceMax}
        minInputValue={minInputValue}
        maxInputValue={maxInputValue}
        priceRangesMin={priceRangesMin}
        priceRangesMax={priceRangesMax}
        onMinInputChange={handleMinInputChange}
        onMaxInputChange={handleMaxInputChange}
        onMinPriceSelect={handleMinPriceSelect}
        onMaxPriceSelect={handleMaxPriceSelect}
        onApply={handleFilterApply}
      />

      <SearchButton 
        variant="contained" 
        startIcon={<SearchIcon />}
        onClick={handleSearch}
      >
        {t('search')}
      </SearchButton>
    </SearchContainer>
  );
};

export default SearchBar;